import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Camera, ClipboardList, Download, FileText, MapPin, Pencil, Plus, Save, Trash2, Truck, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { api } from '../../services/api';
import SignaturePad from '../../Components/Common/SignaturePad';
import senerfiFormato from '../../assets/images/Senerfi-formato.png';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

const IS_SIG_MOBILE = String(process.env.REACT_APP_APP_VARIANT || '').trim().toLowerCase() === 'sig_mobile';

const YES_NO_NA_OPTIONS = [
  { value: '', label: 'Seleccione...' },
  { value: 'SI', label: 'Si' },
  { value: 'NO', label: 'No' },
  { value: 'N/A', label: 'N/A' },
];

const YES_NO_OPTIONS = [
  { value: '', label: 'Seleccione...' },
  { value: 'SI', label: 'Si' },
  { value: 'NO', label: 'No' },
];

const CONFORMITY_OPTIONS = [
  { value: '', label: 'Seleccione...' },
  { value: 'C', label: 'Conforme' },
  { value: 'NC', label: 'No conforme' },
  { value: 'N/A', label: 'No aplica' },
];

const BOTIQUIN_VALORACION_OPTIONS = [
  { value: '', label: 'Seleccione...' },
  { value: 'C', label: 'C' },
  { value: 'N', label: 'N' },
  { value: 'N/A', label: 'N/A' },
];

const GOOD_BAD_NA_OPTIONS = [
  { value: '', label: 'Seleccione...' },
  { value: 'B', label: 'Bueno' },
  { value: 'M', label: 'Malo' },
  { value: 'NA', label: 'NA' },
];

function todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function fmtDateDisplay(value) {
  const raw = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw || '';
  return `${raw.slice(8, 10)}/${raw.slice(5, 7)}/${raw.slice(0, 4)}`;
}

function parseJsonObject(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

async function assetToDataUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function safeSlug(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
}

function rowsFromStrings(items) {
  return items.map((label, index) => ({ id: `row_${index + 1}`, label }));
}

function compressImageFile(file, maxWidth = 800, quality = 0.6) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer la foto.'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('No se pudo procesar la foto.'));
      img.onload = () => {
        const scale = Math.min(1, maxWidth / (img.width || maxWidth));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = String(reader.result || '');
    };
    reader.readAsDataURL(file);
  });
}

// Campo de firma dibujada. Si el valor guardado es texto antiguo (de antes de
// esta función), se muestra como referencia sin romper; si es una firma ya
// dibujada (data URL), se previsualiza arriba del recuadro para volver a firmar.
function SignatureField({ value, onChange }) {
  const isDataUrl = typeof value === 'string' && value.startsWith('data:image');
  return (
    <div className="space-y-1">
      {isDataUrl ? (
        <div>
          <img src={value} alt="Firma guardada" className="border rounded-lg bg-white" style={{ height: 70 }} />
          <div className="text-xs text-gray-400">Vuelve a firmar abajo para reemplazar la firma guardada.</div>
        </div>
      ) : null}
      {value && !isDataUrl ? (
        <div className="text-xs text-gray-500">Valor anterior (texto): {value}</div>
      ) : null}
      <SignaturePad onChange={onChange} height={110} />
    </div>
  );
}

// Selector de fotos por ítem (hasta `max`), comprimidas en el cliente antes de
// guardarse como data URL dentro del mismo JSON flexible de la fila.
function PhotoPicker({ photos, onAdd, onRemove, max = 3 }) {
  const inputRef = useRef(null);
  const [error, setError] = useState('');
  const list = Array.isArray(photos) ? photos : [];

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) {
      setError('No se detectó ninguna foto (¿se canceló la cámara?). Intenta de nuevo.');
      return;
    }
    try {
      setError('');
      const dataUrl = await compressImageFile(file);
      if (!dataUrl || !dataUrl.startsWith('data:image')) {
        setError('La foto se procesó vacía, prueba de nuevo.');
        return;
      }
      onAdd(dataUrl);
    } catch (err) {
      console.error('Error procesando foto', err);
      setError(err?.message ? `No se pudo agregar la foto: ${err.message}` : 'No se pudo agregar la foto.');
    }
  };

  return (
    <div className="space-y-1">
      {list.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {list.map((src, idx) => (
            <div key={idx} className="relative">
              <img src={src} alt={`Foto ${idx + 1}`} className="w-16 h-16 object-cover rounded border border-gray-300" />
              <button
                type="button"
                onClick={() => onRemove(idx)}
                className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow"
                aria-label="Quitar foto"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {list.length < max ? (
        <>
          <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} />
          <button
            type="button"
            onClick={() => inputRef.current && inputRef.current.click()}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-300 text-gray-700 text-xs hover:bg-gray-50"
          >
            <Camera size={14} /> Agregar foto
          </button>
        </>
      ) : null}
      {error ? <div className="text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div> : null}
    </div>
  );
}

const EQUIPOS_ALTURAS_ARNES_ITEMS = [
  'Cintas / Correas - Hoyos o agujeros',
  'Cintas / Correas - Cintas deshilachadas',
  'Cintas / Correas - Desgastadas',
  'Cintas / Correas - Talladuras',
  'Cintas / Correas - Hay torsión (fibras duras o deformadas).',
  'Cintas / Correas - Contaminaciòn excesiva por suciedad.',
  'Cintas / Correas - Quemaduras por soldadura, cigarrillo, etc.',
  'Cintas / Correas - Salpicadura de pintura y rigidez en cinta.',
  'Cintas / Correas - Degradaciòn por U.V. (pèrdida de color y superficie quebradiza).',
  'Cintas / Correas - Ataque por sustancias químicas',
  'Costuras - Abiertas',
  'Costuras - Hebras sueltas',
  'Costuras - Reventadas',
  'Costuras - Otros',
  'Herrajes, hebillas y argollas en "D" - Desgaste en los extremos',
  'Herrajes, hebillas y argollas en "D" - Deformación',
  'Herrajes, hebillas y argollas en "D" - Fisuras, golpes, hundimientos.',
  'Herrajes, hebillas y argollas en "D" - Aristas, bordes filosos o arrugados.',
  'Herrajes, hebillas y argollas en "D" - Otros',
  'Estado general del arnés (Bueno / Malo)',
];

const EQUIPOS_ALTURAS_ESLINGA_ITEMS = [
  'Absorbedor - Agujeros',
  'Absorbedor - Rasgaduras',
  'Absorbedor - Costuras sueltas o reventadas.',
  'Absorbedor - Deterioro',
  'Absorbedor - Hebras rotas',
  'Absorbedor - Contaminaciòn por suciedad',
  'Absorbedor - Quemaduras por soldadura o cigarrillo.',
  'Absorbedor - Salpicadura de pintura y rigidez en cinta.',
  'Absorbedor - Absorbedor abierto, desgarrado o descosido.',
  'Reata / Cuerda - Hoyos o agujeros',
  'Reata / Cuerda - Cintas deshilachadas',
  'Reata / Cuerda - Desgastadas',
  'Reata / Cuerda - Talladuras',
  'Reata / Cuerda - Hay torsión (fibras duras o deformadas)',
  'Reata / Cuerda - Contaminaciòn excesiva por suciedad',
  'Reata / Cuerda - Quemaduras por soldadura, cigarrillo, etc.',
  'Reata / Cuerda - Salpicadura de pintura y rigidez en cinta.',
  'Reata / Cuerda - Degradaciòn por U.V. (pèrdida de color y superficie quebradiza)',
  'Reata / Cuerda - Ataque por sustancias químicas',
  'Reata / Cuerda - Otros',
  'Herrajes, hebillas y mosquetones - Desgaste en los extremos',
  'Herrajes, hebillas y mosquetones - Corrosión',
  'Herrajes, hebillas y mosquetones - Deformación',
  'Herrajes, hebillas y mosquetones - Fisuras, golpes, hundimientos.',
  'Herrajes, hebillas y mosquetones - Aristas, bordes filosos o arrugados.',
  'Herrajes, hebillas y mosquetones - Otros',
  'Estado general de la eslinga (Bueno / Malo)',
];

const PUESTO_TRABAJO_ITEMS = [
  'Se tiene control para evitar reflejos en la pantalla de los computadores',
  '¿La pantalla se encuentra ubicada de frente al trabajador?',
  '¿La pantalla es ajustable en giro, inclinación y altura?',
  '¿La línea superior de la pantalla esta horizontal a los ojos formando un ángulo de cero grados?',
  'Se tiene un mantenimiento adecuado de las lámparas',
  'Las áreas de trabajo cumplen con los niveles de confort acústico',
  'Las herramientas de trabajo (sacaganchos, grapadoras, perforadoras) se encuentran en buen estado',
  'Los cables eléctricos se encuentran protegidos y canalizados',
  'Las instalaciones eléctricas están protegidas',
  'Las instalaciones eléctricas cuentan con swiches y tomacorrientes',
  '¿Las sillas ergonomicas, son estables y se encuentran en buen estado?',
  'Se aplica una superficie mínima por trabajador de dos metros cuadrados',
  'Los puestos de trabajo tienen el mouse y el teclado en una misma superficie',
  'Las oficinas estan libres de elementos almacenados debajo de los escritorios',
  'El escritorio tiene solo los elementos necesarios, no tiene objetos personales, está libre de vasos, pocillos vacios, residuos de comida',
  'Los computadores están limpios, en buen estado y adecuados para su funcionamiento',
  'La oficina esta libre de afiches, listas, papeles, etc, pegados en las paredes',
  'Las carpetas estan limpias, ordenadas, en buen estado y debidamente rotuladas',
  'Existe confort termico',
  'Existe confort lumínico',
  '¿El área se encuentra en orden y aseo?',
  'Los pisos estan libres de obstaculos',
];

const BOTIQUIN_ITEMS = [
  'ANTISÉPTICOS - Isodine solución y/o Yodopavina',
  'ANTISÉPTICOS - Jabón antiséptico o gel antibacterial',
  'ANTISÉPTICOS - Alcohol antiséptico 275ml',
  'ANTISÉPTICOS - Solución salina 250cc',
  'ANTISÉPTICOS - Apósitos',
  'ANTISÉPTICOS - Baja lenguas paquete',
  'ANTISÉPTICOS - Aplicadores',
  'ANTISÉPTICOS - Curas adhesivas',
  'ANTISÉPTICOS - Esparadrapo de tela rollo 4"',
  'ANTISÉPTICOS - Gasas estériles 3*3',
  'ANTISÉPTICOS - Micropore piel 2x5 (yardas)',
  'ANTISÉPTICOS - Mascara RCP',
  'ANTISÉPTICOS - Vendas elásticas 2*5" (yardas)',
  'ANTISÉPTICOS - Vendas elásticas 3*5" (yardas)',
  'ANTISÉPTICOS - Vendas elásticas 5*5" (yardas)',
  'ANTISÉPTICOS - Vendas de algodón 3*5" (yardas)',
  'ANTISÉPTICOS - Vendas de algodón 5*5" (yardas)',
  'ANTISÉPTICOS - Venda triangular',
  'ANTISÉPTICOS - Apósito ocular',
  'ANTISÉPTICOS - Guantes de látex para examen',
  'ANTISÉPTICOS - Jeringa 5cm',
  'ANTISÉPTICOS - Tapabocas',
  'ANTISÉPTICOS - Tijeras de trauma',
  'OTROS ELEMENTOS - Pito o silbato',
  'OTROS ELEMENTOS - Radio con pilas de repuesto',
  'OTROS ELEMENTOS - Termómetro digital',
  'OTROS ELEMENTOS - Algodón',
  'OTROS ELEMENTOS - Suero oral',
  'OTROS ELEMENTOS - Linterna',
  'OTROS ELEMENTOS - Pilas de repuesto para linterna',
  'OTROS ELEMENTOS - Chaleco',
  'OTROS ELEMENTOS - Inmovilizador vertical',
  'OTROS ELEMENTOS - Inmovilizador de miembros superiores',
  'OTROS ELEMENTOS - Inmovilizador de miembros inferiores',
  'OTROS ELEMENTOS - Listado teléfonos de emergencias',
  'OTROS ELEMENTOS - Manual de primeros auxilios',
  'OTROS ELEMENTOS - Maletín',
  'OTROS ELEMENTOS - Formato de control de elementos',
  'OTROS ELEMENTOS - Bolsas rojas',
];

const VEHICULO_ITEMS = [
  { group: 'DOCUMENTOS', items: [
    'Licencia de conducción',
    'Tarjeta de propiedad',
    'Seguro obligatorio (SOAT)',
    'Póliza contractual y extracontractual',
    'Revisión tecno mecánica',
    'Certificado de izaje',
    'Carnet (CEO)',
  ] },
  { group: 'CARROCERÍA', items: [
    'Logotipo (CEO)',
    'Latonería y pintura',
    'Cojinería',
    'Vidrios',
    'Espejos retrovisores (laterales y central)',
    'Aceite, agua, líquidos',
    'Estado de carrocería',
  ] },
  { group: 'INDICADORES Y/O MEDIDORES', items: [
    'Velocímetro',
    'Tacómetro',
    'Medidor de carga de la batería',
    'Medidor de combustible',
    'Indicador de temperatura de motor',
  ] },
  { group: 'SISTEMA OPERATIVO', items: [
    'Luces altas',
    'Luces bajas',
    'Luces de parqueo',
    'Direccionales delanteras y traseras',
    'Luces de reversa',
    'Luces de freno (Stop)',
    'Limpiabrisas o plumillas',
    'Sistema de lavado de parabrisas',
    'Bocina',
    'Pito de reversa',
    'Rines',
    'Brazo hidráulico',
    'Estado mangueras hidráulicas',
    'Estado racores (uniones) mangueras',
    'Vaso canasta (estado y aislamiento), vigencia',
    'Estado de bomba hidráulica',
    'Frenos',
    'Freno de emergencia',
    'Estrobos certificados',
  ] },
  { group: 'EQUIPO DE PREVENCIÓN Y SEGURIDAD', items: [
    'Gato',
    'Conos (8) o triángulos reflectivos (2)',
    'Polines',
    'Tacos',
    'Llanta de repuesto',
    'Juego de herramienta',
    'Cruceta',
    'Cinturones de seguridad funcionales',
    'Linterna',
    'Extintor de incendio ABC (fecha)',
    'Botiquín de primeros auxilios',
  ] },
  { group: 'TRANSPORTE DE PERSONAL', items: [
    'Condiciones de orden y aseo',
  ] },
];

const EXTINTORES_ITEMS = [
  'Cilindro',
  'Pintura',
  'Válvula',
  'Manigueta',
  'Manguera',
  'Manómetro',
  'Carga en manómetro',
  'Pasador asegurado',
  'Calcomanía',
  'Corneta',
  'Boquilla',
  'Sello de garantía',
  'Soporte',
  'Ubicación',
  'Fecha vencimiento',
];

const ESCALERA_ELEMENTS_ITEMS = [
  'LARGUEROS (ausencia de roturas, rajadura, afloramiento en fibra de vidrio, etc.)',
  'PELDAÑOS (todos fijos y sin movimiento)',
  'MANILA (existencia y condiciones)',
  'ZAPATAS DE SEGURIDAD (goma antideslizante, bulón, tuerca, pivoteo)',
  'TRABAPELDAÑOS (sin rotura y resortes funcionan)',
  'APOYA POSTE (ausencia de deformaciones)',
  'POLEA (funcionalidad)',
  'PIES DE AMIGO (existencia y buen estado)',
  'UÑAS (presencia y estado)',
  'PRUEBA DE FUNCIONALIDAD (sin barro, grasa, aceite, pintura u otros que afecten la dielectricidad)',
];

const ESCALERA_DOC_ITEMS = [
  'La escalera es dieléctrica en fibra de vidrio',
  'Identificación legible en la escalera',
  'Cuenta con la ficha técnica y certificación del fabricante',
  'Estado de los portaescaleras en los vehículos',
  'La escalera cuenta con hoja de vida',
];

const IPAL_CHECKLIST_ROWS = [
  { code: 'A,1', label: 'Usan los EPP completos en el momento del alistamiento' },
  { code: 'A,2', label: 'Se realiza inspección al vehículo antes de salir de la base y se diligencia la lista de chequeo' },
  { code: 'A,3', label: 'El vehículo permanece apagado en el momento del alistamiento' },
  { code: 'A,4', label: 'El vehículo permanece en buenas condiciones técnico-mecánicas' },
  { code: 'A,5', label: 'Se verifican los documentos de los trabajadores (ARL, carnet de la empresa)' },
  { code: 'A,6', label: 'Usa las botas dieléctricas de seguridad en buen estado' },
  { code: 'A,7', label: 'Se cuenta con los respectivos kits (ambiental, señalización, demarcación del área, etc.)' },
  { code: 'A,8', label: 'La brigada cuenta con los procedimientos para T.C.T. en buen estado y legibles' },
  { code: 'A,9', label: 'Los integrantes de la brigada cuentan con condiciones físicas aptas para realizar la labor' },
  { code: 'A,10', label: 'Se tienen los medios de comunicación respectivos' },
  { code: 'A,11', label: 'Documentos del vehículo y del conductor vigentes (tarjeta de propiedad, SOAT, tecno mecánica, licencia, pruebas dieléctricas, etc.)' },

  { code: 'B,1', label: 'Utiliza ropa ignífuga adecuada para la labor en buenas condiciones' },
  { code: 'B,2', label: 'Todo el personal utiliza casco de seguridad con sistema de fijación ignífugo en buen estado' },
  { code: 'B,3', label: 'Utiliza careta con visor para arco eléctrico en buen estado' },
  { code: 'B,4', label: 'Utiliza gafas de seguridad de acuerdo a la labor' },
  { code: 'B,5', label: 'Utiliza guantes de seguridad de acuerdo a la labor' },
  { code: 'B,6', label: 'Utiliza guantes dieléctricos de acuerdo a la tensión nominal en MT con pruebas vigentes' },
  { code: 'B,7', label: 'Utiliza el sistema de protección contra caídas completo y en buen estado' },
  { code: 'B,8', label: 'Utiliza protección facial (monja/solar) (escafandra)' },
  { code: 'B,9', label: 'Utiliza mangas dieléctricas en buen estado con pruebas vigentes' },

  { code: 'C,1', label: 'Se realiza inspección al vehículo antes de salir de la base y se diligencia la lista de chequeo' },
  { code: 'C,2', label: 'El vehículo es apto para el transporte del personal' },
  { code: 'C,3', label: 'El vehículo cuenta con kit de carretera completo en buen estado y organizado' },
  { code: 'C,4', label: 'El vehículo cuenta con botiquín y camilla' },
  { code: 'C,5', label: 'El vehículo cuenta con extintores vigentes' },

  { code: 'D,1', label: 'Se estabiliza el vehículo (bajar los gatos) y se aterriza' },
  { code: 'D,2', label: 'Se colocan luces/estacionarias/licuadora en los trabajos a realizar' },
  { code: 'D,3', label: 'Realiza inspección al sistema hidráulico incluido niveles de aceite' },
  { code: 'D,4', label: 'Se manipula adecuadamente el brazo de la canasta evitando contacto con líneas o realizando cubrimiento inmediato' },
  { code: 'D,5', label: 'El movimiento del brazo de la canasta es guiado entre coordinador y liniero' },
  { code: 'D,6', label: 'El liniero usa la línea de vida asegurada al punto de conexión del carro canasta' },

  { code: 'E,1', label: 'Se colocan estacionarias, luces y licuadoras en los trabajos' },
  { code: 'E,2', label: 'Aplica el freno de seguridad antes de operar el sistema hidráulico' },
  { code: 'E,3', label: 'Se estabiliza el vehículo al bajar los gatos y se aterriza con profundidad adecuada' },
  { code: 'E,4', label: 'Se analiza la actividad para operar el sistema de la grúa, utilizando estrobo o tijera' },
  { code: 'E,5', label: 'Se manipula adecuadamente el brazo y extensión de la grúa' },
  { code: 'E,6', label: 'Se verifica que el área de trabajo se encuentre despejada' },

  { code: 'F,1', label: 'Se inspecciona el poste o estructura antes de maniobrar o intervenir' },
  { code: 'F,2', label: 'Se asciende y desciende la escalera de forma adecuada, utilizando el equipo de rescate' },
  { code: 'F,3', label: 'Se amarra correctamente la escalera al poste' },
  { code: 'F,4', label: 'Se transporta y manipula la escalera en forma adecuada' },
  { code: 'F,5', label: 'Asciende y desciende con pretales de forma adecuada utilizando equipo anticaídas o arnés' },

  { code: 'G,1', label: 'Los equipos como motosierras se encuentran en buen estado' },
  { code: 'G,2', label: 'Herramientas como desjarretadora, pértiga y machete se encuentran en buen estado' },
  { code: 'G,3', label: 'Se utiliza protección tipo pants para actividades de repique' },
  { code: 'G,4', label: 'Se utiliza protección auditiva tipo copa' },
  { code: 'G,5', label: 'Se utiliza careta en actividades de poda con motosierra' },
  { code: 'G,6', label: 'En repique se utiliza correctamente el guante anticorte' },
  { code: 'G,7', label: 'Se realizan cortes técnicos de acuerdo con el procedimiento de poda' },
  { code: 'G,8', label: 'Se aplica cicatrizante en los cortes realizados' },
  { code: 'G,9', label: 'La cadena de la motosierra se desinfecta con alcohol entre individuos arbóreos' },
  { code: 'G,10', label: 'El personal cuenta con competencia vigente de poda y tala de árboles' },

  { code: 'H,1', label: 'Se cumplen las distancias mínimas de seguridad para T.C.T.' },
  { code: 'H,2', label: 'Se comunican con el centro de control para solicitar la prevención del circuito' },
  { code: 'H,3', label: 'La brigada aplica recomendaciones del área HSE' },
  { code: 'H,4', label: 'Se cuenta con una orden de trabajo' },
  { code: 'H,5', label: 'Claridad de especificaciones técnicas y trabajos a realizar' },
  { code: 'H,6', label: 'Se cumplen procedimientos operativos de forma secuencial' },
  { code: 'H,7', label: 'Se utilizan herramientas y equipos adecuados para el T.C.T.' },
  { code: 'H,8', label: 'Se utilizan materiales indicados para los trabajos' },
  { code: 'H,9', label: 'El jefe de brigada supervisa de manera efectiva la ejecución de los T.C.T.' },
  { code: 'H,10', label: 'El jefe de brigada conoce condiciones físicas del personal antes de iniciar los T.C.T.' },
  { code: 'H,11', label: 'Sabe actuar en caso de accidente, incidente o emergencia en el trabajo' },

  { code: 'I,1', label: 'Manejo de aceites y manejo de residuos sólidos' },
  { code: 'I,2', label: 'Sabe actuar en caso de una emergencia ambiental' },
  { code: 'I,3', label: 'Equipos con aceite transportados sobre bandejas metálicas y asegurados' },
  { code: 'I,4', label: 'Sabe identificar si el aceite dieléctrico puede contener PCBs' },
  { code: 'I,5', label: 'Residuos en bolsas rojas bien amarradas y marcadas' },
  { code: 'I,6', label: 'Kit ambiental completo (manga oleofílica, aserrín/arena, paño absorbente, guantes nitrilo)' },
  { code: 'I,7', label: 'Residuos separados según composición' },
  { code: 'I,8', label: 'Orden y aseo del sitio de trabajo' },
  { code: 'I,9', label: 'Vehículos limpios y aseados' },
  { code: 'I,10', label: 'Residuos peligrosos almacenados y recogidos adecuadamente' },
  { code: 'I,11', label: 'Almacenamiento y reintegro del material sobrante' },

  { code: 'J,1', label: 'Escombros libres de cinta de señalización' },
  { code: 'J,2', label: 'Residuos apilados a 1 m de distancia para recolección' },
  { code: 'J,3', label: 'Escombros recogidos y apilados' },
  { code: 'J,4', label: 'Área de trabajo en orden y aseo' },
  { code: 'J,5', label: 'No uso de elementos metálicos ni celular en el liniero' },

  { code: 'K,1', label: 'Cinta, conos, colombinas, vallas para señalizar y demarcar' },
  { code: 'K,2', label: 'Área debidamente señalizada y demarcada' },
  { code: 'K,3', label: 'Vehículo señalizado y demarcado' },

  { code: 'L,1', label: 'Riesgos identificados en el ATS' },
  { code: 'L,2', label: 'Permiso de trabajo de alto riesgo diligenciado con firmas' },
  { code: 'L,3', label: 'Charla técnica y de seguridad evaluando riesgos en sitio' },
  { code: 'L,4', label: 'Tablero operativo bien diligenciado' },
  { code: 'L,5', label: 'Competencias para trabajar en alturas' },
  { code: 'L,6', label: 'Personal habilitado' },
  { code: 'L,7', label: 'Personal se desempeña en el cargo para el cual fue contratado' },
  { code: 'L,8', label: 'Análisis preoperacional y evaluación de riesgos evidenciada en ATS' },

  { code: 'M,1', label: 'Etiquetado SGA en productos químicos utilizados' },
  { code: 'M,2', label: 'Fichas de datos de seguridad actualizadas (físico y digital) de los productos químicos' },
  { code: 'M,3', label: 'Recipientes de sustancias químicas adecuados (no recipientes de alimentos)' },
  { code: 'M,4', label: 'Productos químicos almacenados y cerrados adecuadamente evitando derrames' },
  { code: 'M,5', label: 'Vehículo que transporta mercancías peligrosas (transformadores) con pictogramas clase 9 y número ONU' },
];

const INSPECCIONES_SST_META = {
  equipos_alturas: {
    title: 'Equipo de alturas',
    description: 'Inspección de equipos de trabajo en alturas.',
    icon: AlertTriangle,
    gradient: 'linear-gradient(135deg, #DC2626 0%, #F97316 100%)',
    formCode: 'FT-SST-007',
    version: '01',
    summaryColumns: [
      { key: 'inspection_date', label: 'Fecha' },
      { key: 'equipment1_internal_code', label: 'Equipo 1 Código' },
      { key: 'equipment2_internal_code', label: 'Equipo 2 Código' },
      { key: 'equipment3_internal_code', label: 'Equipo 3 Código' },
    ],
    fieldGroups: [
      {
        title: 'Datos generales',
        fields: [
          { key: 'inspection_date', label: 'Fecha de inspección', type: 'date', required: true, defaultValue: todayYmd() },
        ],
      },
      {
        title: 'Equipo 1',
        fields: [
          { key: 'equipment1_brand', label: 'Marca del equipo', type: 'text' },
          { key: 'equipment1_fab_date', label: 'Fecha de fabricación', type: 'text' },
          { key: 'equipment1_serial', label: 'Serial', type: 'text' },
          { key: 'equipment1_internal_code', label: 'Código interno', type: 'text' },
          { key: 'equipment1_cert_date', label: 'Fecha de certificación', type: 'text' },
          { key: 'equipment1_responsible_sign', label: 'Firma responsable del equipo', type: 'signature' },
        ],
      },
      {
        title: 'Equipo 2',
        fields: [
          { key: 'equipment2_brand', label: 'Marca del equipo', type: 'text' },
          { key: 'equipment2_fab_date', label: 'Fecha de fabricación', type: 'text' },
          { key: 'equipment2_serial', label: 'Serial', type: 'text' },
          { key: 'equipment2_internal_code', label: 'Código interno', type: 'text' },
          { key: 'equipment2_cert_date', label: 'Fecha de certificación', type: 'text' },
          { key: 'equipment2_responsible_sign', label: 'Firma responsable del equipo', type: 'signature' },
        ],
      },
      {
        title: 'Equipo 3',
        fields: [
          { key: 'equipment3_brand', label: 'Marca del equipo', type: 'text' },
          { key: 'equipment3_fab_date', label: 'Fecha de fabricación', type: 'text' },
          { key: 'equipment3_serial', label: 'Serial', type: 'text' },
          { key: 'equipment3_internal_code', label: 'Código interno', type: 'text' },
          { key: 'equipment3_cert_date', label: 'Fecha de certificación', type: 'text' },
          { key: 'equipment3_responsible_sign', label: 'Firma responsable del equipo', type: 'signature' },
        ],
      },
      {
        title: 'Firmas responsables de la inspección',
        fields: [
          { key: 'inspector_1_name', label: 'Oper 1 - Nombre', type: 'text' },
          { key: 'inspector_1', label: 'Oper 1 - Firma', type: 'signature' },
          { key: 'inspector_2_name', label: 'Oper 2 - Nombre', type: 'text' },
          { key: 'inspector_2', label: 'Oper 2 - Firma', type: 'signature' },
          { key: 'inspector_3_name', label: 'Oper 3 - Nombre', type: 'text' },
          { key: 'inspector_3', label: 'Oper 3 - Firma', type: 'signature' },
        ],
      },
    ],
    sections: [
      {
        key: 'arnes',
        type: 'fixed_rows',
        title: 'Arnés',
        columns: [
          { key: 'eq1_status', label: 'Eq1 (SI/NO)', type: 'select', options: YES_NO_OPTIONS },
          { key: 'eq1_obs', label: 'Eq1 Obs', type: 'text' },
          { key: 'eq2_status', label: 'Eq2 (SI/NO)', type: 'select', options: YES_NO_OPTIONS },
          { key: 'eq2_obs', label: 'Eq2 Obs', type: 'text' },
          { key: 'eq3_status', label: 'Eq3 (SI/NO)', type: 'select', options: YES_NO_OPTIONS },
          { key: 'eq3_obs', label: 'Eq3 Obs', type: 'text' },
        ],
        rows: rowsFromStrings(EQUIPOS_ALTURAS_ARNES_ITEMS),
      },
      {
        key: 'eslinga',
        type: 'fixed_rows',
        title: 'Eslinga de posicionamiento',
        columns: [
          { key: 'eq1_status', label: 'Eq1 (SI/NO)', type: 'select', options: YES_NO_OPTIONS },
          { key: 'eq1_obs', label: 'Eq1 Obs', type: 'text' },
          { key: 'eq2_status', label: 'Eq2 (SI/NO)', type: 'select', options: YES_NO_OPTIONS },
          { key: 'eq2_obs', label: 'Eq2 Obs', type: 'text' },
          { key: 'eq3_status', label: 'Eq3 (SI/NO)', type: 'select', options: YES_NO_OPTIONS },
          { key: 'eq3_obs', label: 'Eq3 Obs', type: 'text' },
        ],
        rows: rowsFromStrings(EQUIPOS_ALTURAS_ESLINGA_ITEMS),
      },
    ],
  },
  puesto_trabajo: {
    title: 'Puesto de trabajo',
    description: 'Inspección de puesto de trabajo.',
    icon: ClipboardList,
    gradient: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)',
    formCode: 'FT-SIG-109',
    version: '02',
    summaryColumns: [
      { key: 'inspection_date', label: 'Fecha' },
      { key: 'person_name', label: 'Nombre' },
      { key: 'inspection_area', label: 'Área' },
      { key: 'contract_name', label: 'Contrato' },
    ],
    fieldGroups: [
      {
        title: 'Datos de la inspección',
        fields: [
          { key: 'person_name', label: 'Nombre', type: 'text', required: true },
          { key: 'inspection_date', label: 'Fecha', type: 'date', required: true, defaultValue: todayYmd() },
          { key: 'inspection_area', label: 'Área de inspección', type: 'text' },
          { key: 'contract_name', label: 'Contrato', type: 'text' },
        ],
      },
      {
        title: 'Firmas',
        fields: [
          { key: 'performed_by_name', label: 'Inspeccionado por - Nombre', type: 'text' },
          { key: 'performed_by_role', label: 'Inspeccionado por - Cargo', type: 'text' },
          { key: 'performed_by_signature', label: 'Inspeccionado por - Firma', type: 'signature' },
          { key: 'received_by_name', label: 'Inspección recibida por - Nombre', type: 'text' },
          { key: 'received_by_role', label: 'Inspección recibida por - Cargo', type: 'text' },
          { key: 'received_by_signature', label: 'Inspección recibida por - Firma', type: 'signature' },
        ],
      },
    ],
    sections: [
      {
        key: 'checklist',
        type: 'fixed_rows',
        title: 'Condiciones',
        columns: [
          { key: 'status', label: 'Cumple', type: 'select', options: YES_NO_OPTIONS },
          { key: 'observation', label: 'Observación', type: 'text' },
        ],
        rows: rowsFromStrings(PUESTO_TRABAJO_ITEMS),
      },
    ],
  },
  botiquin: {
    title: 'Botiquín',
    description: 'Inspección de botiquín fijo.',
    icon: AlertTriangle,
    gradient: 'linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)',
    formCode: 'FT-SIG-112',
    version: '05',
    summaryColumns: [
      { key: 'inspection_date', label: 'Fecha' },
      { key: 'contract_name', label: 'Contrato' },
      { key: 'area_type', label: 'Área' },
      { key: 'inspection_time', label: 'Hora' },
    ],
    fieldGroups: [
      {
        title: 'Datos de la inspección',
        fields: [
          { key: 'inspection_date', label: 'Fecha', type: 'date', required: true, defaultValue: todayYmd() },
          { key: 'inspection_time', label: 'Hora', type: 'time' },
          { key: 'contract_name', label: 'Contrato', type: 'text' },
          { key: 'area_type', label: 'Área de inspección', type: 'select', options: [
            { value: '', label: 'Seleccione...' },
            { value: 'OFICINA', label: 'Oficina' },
            { value: 'ALMACEN', label: 'Almacén' },
            { value: 'OTROS', label: 'Otros' },
          ] },
          { key: 'area_other_detail', label: 'Otros (detalle)', type: 'text' },
        ],
      },
      {
        title: 'Firmas',
        fields: [
          { key: 'performed_by_name', label: 'Realizado por - Nombre', type: 'text' },
          { key: 'performed_by_id', label: 'Realizado por - Cédula', type: 'text' },
          { key: 'performed_by_role', label: 'Realizado por - Cargo', type: 'text' },
          { key: 'performed_by_signature', label: 'Realizado por - Firma', type: 'signature' },
          { key: 'received_by_name', label: 'Recibida por - Nombre', type: 'text' },
          { key: 'received_by_id', label: 'Recibida por - Cédula', type: 'text' },
          { key: 'received_by_role', label: 'Recibida por - Cargo', type: 'text' },
          { key: 'received_by_signature', label: 'Recibida por - Firma', type: 'signature' },
        ],
      },
    ],
    sections: [
      {
        key: 'items',
        type: 'fixed_rows',
        title: 'Elementos',
        columns: [
          { key: 'quantity', label: 'Cantidad', type: 'text' },
          { key: 'status', label: 'Valoración', type: 'select', options: BOTIQUIN_VALORACION_OPTIONS },
          { key: 'expiration_date', label: 'Fecha vencimiento', type: 'date' },
          { key: 'observation', label: 'Observaciones', type: 'text' },
        ],
        rows: rowsFromStrings(BOTIQUIN_ITEMS),
      },
    ],
  },
  epp: {
    title: 'EPP',
    description: 'Inspección de EPP.',
    icon: ClipboardList,
    gradient: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
    formCode: 'FT-SIG-113',
    version: '05',
    summaryColumns: [
      { key: 'inspection_date', label: 'Fecha' },
      { key: 'inspection_place', label: 'Lugar' },
      { key: 'project_name', label: 'Proyecto' },
      { key: 'brigade_name', label: 'Brigada' },
    ],
    fieldGroups: [
      {
        title: 'Datos de la inspección',
        fields: [
          { key: 'inspection_date', label: 'Fecha', type: 'date', required: true, defaultValue: todayYmd() },
          { key: 'inspection_place', label: 'Lugar de inspección', type: 'text' },
          { key: 'project_name', label: 'Proyecto', type: 'text' },
          { key: 'brigade_name', label: 'Brigada', type: 'text' },
        ],
      },
      {
        title: 'Inspección realizada por',
        fields: [
          { key: 'performed_by_name', label: 'Nombre completo', type: 'text' },
          { key: 'performed_by_role', label: 'Cargo', type: 'text' },
          { key: 'performed_by_signature', label: 'Firma', type: 'signature' },
        ],
      },
    ],
    sections: [
      {
        key: 'rows',
        type: 'dynamic_rows',
        title: 'Registro',
        columns: [
          { key: 'date', label: 'Fecha', type: 'date' },
          { key: 'responsible_name', label: 'Responsable EPP (Nombre)', type: 'text' },
          { key: 'responsible_id', label: 'Cédula', type: 'text' },
          { key: 'casco_i', label: 'Casco tipo I', type: 'select', options: CONFORMITY_OPTIONS },
          { key: 'casco_ii', label: 'Casco tipo II', type: 'select', options: CONFORMITY_OPTIONS },
          { key: 'barbuquejo', label: 'Barbuquejo', type: 'select', options: CONFORMITY_OPTIONS },
          { key: 'gafas', label: 'Gafas', type: 'select', options: CONFORMITY_OPTIONS },
          { key: 'tapabocas', label: 'Tapabocas', type: 'select', options: CONFORMITY_OPTIONS },
          { key: 'auditivos', label: 'Protectores auditivos', type: 'select', options: CONFORMITY_OPTIONS },
          { key: 'guantes_ing', label: 'Guantes ingeniero corto', type: 'select', options: CONFORMITY_OPTIONS },
          { key: 'guante_vaqueta', label: 'Guante vaqueta', type: 'select', options: CONFORMITY_OPTIONS },
          { key: 'guantes_dielectricos', label: 'Guantes dieléctricos', type: 'select', options: CONFORMITY_OPTIONS },
          { key: 'guantines', label: 'Guantines', type: 'select', options: CONFORMITY_OPTIONS },
          { key: 'botas', label: 'Botas cuero/dieléctricas', type: 'select', options: CONFORMITY_OPTIONS },
          { key: 'chaleco', label: 'Chaleco', type: 'select', options: CONFORMITY_OPTIONS },
          { key: 'otros', label: 'Otros', type: 'text' },
          { key: 'signature', label: 'Firma', type: 'signature' },
        ],
        initialRows: 5,
      },
    ],
  },
  ipal: {
    title: 'IPAL',
    description: 'Indicador de prevención de accidentes laborales.',
    icon: AlertTriangle,
    gradient: 'linear-gradient(135deg, #EA580C 0%, #EF4444 100%)',
    formCode: 'FT-SIG-118',
    version: '2',
    summaryColumns: [
      { key: 'inspection_date', label: 'Fecha' },
      { key: 'work_order', label: 'OT' },
      { key: 'ticket', label: 'Ticket' },
      { key: 'brigade_leader', label: 'Jefe brigada' },
    ],
    fieldGroups: [
      {
        title: 'Datos de la inspección',
        fields: [
          { key: 'inspection_date', label: 'Fecha de la inspección', type: 'date', required: true, defaultValue: todayYmd() },
          { key: 'start_time', label: 'Hora inicial', type: 'time' },
          { key: 'end_time', label: 'Hora final', type: 'time' },
          { key: 'work_order', label: 'Orden de trabajo', type: 'text' },
          { key: 'ticket', label: 'Ticket', type: 'text' },
          { key: 'consigna', label: 'Consigna', type: 'text' },
          { key: 'tension_level', label: 'Nivel de tensión', type: 'text' },
          { key: 'tct', label: 'T.C.T.', type: 'text' },
          { key: 'contact', label: 'Contacto', type: 'text' },
          { key: 'vehicle_plate', label: 'Placa del vehículo', type: 'text' },
          { key: 'brigade_leader', label: 'Jefe de brigada', type: 'text' },
          { key: 'department', label: 'Departamento', type: 'text' },
          { key: 'municipality', label: 'Municipio', type: 'text' },
          { key: 'zone', label: 'Zona', type: 'text' },
          { key: 'address', label: 'Dirección / sector / barrio', type: 'text' },
        ],
      },
      {
        title: 'Firmas',
        fields: [
          { key: 'performed_by_name', label: 'Inspección realizada por - Nombre', type: 'text' },
          { key: 'performed_by_role', label: 'Inspección realizada por - Cargo', type: 'text' },
          { key: 'performed_by_signature', label: 'Inspección realizada por - Firma', type: 'signature' },
          { key: 'received_by_name', label: 'Inspección recibida por - Nombre', type: 'text' },
          { key: 'received_by_role', label: 'Inspección recibida por - Cargo', type: 'text' },
          { key: 'received_by_signature', label: 'Inspección recibida por - Firma', type: 'signature' },
        ],
      },
    ],
    sections: [
      {
        key: 'brigade',
        type: 'dynamic_rows',
        title: 'Datos de la brigada',
        columns: [
          { key: 'id_number', label: 'Cédula', type: 'text' },
          { key: 'full_name', label: 'Nombres y apellidos', type: 'text' },
          { key: 'role_name', label: 'Cargo', type: 'text' },
          { key: 'carnet', label: 'Carnet', type: 'select', options: YES_NO_OPTIONS },
          { key: 'arl', label: 'ARL', type: 'select', options: YES_NO_OPTIONS },
          { key: 'signature', label: 'Firma', type: 'signature' },
        ],
        initialRows: 4,
      },
      {
        key: 'checklist',
        type: 'fixed_rows',
        title: 'Verificación de conductas inseguras en terreno',
        columns: [
          { key: 'status', label: 'Cumple', type: 'select', options: YES_NO_NA_OPTIONS },
          { key: 'observation', label: 'Observación', type: 'text' },
        ],
        rows: IPAL_CHECKLIST_ROWS.map((item, idx) => ({ id: `row_${idx + 1}`, label: `${item.code} - ${item.label}` })),
      },
    ],
  },
  extintores: {
    title: 'Extintores',
    description: 'Inspección de extintores.',
    icon: AlertTriangle,
    gradient: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
    formCode: 'FT-SIG-140',
    version: '02',
    summaryColumns: [
      { key: 'inspection_date', label: 'Fecha' },
      { key: 'project_name', label: 'Proyecto' },
      { key: 'location_text', label: 'Ubicación' },
      { key: 'responsible_name', label: 'Responsable' },
    ],
    fieldGroups: [
      {
        title: 'Datos de la inspección',
        fields: [
          { key: 'inspection_date', label: 'Fecha', type: 'date', required: true, defaultValue: todayYmd() },
          { key: 'project_name', label: 'Proyecto', type: 'text' },
          { key: 'location_text', label: 'Ubicación', type: 'text' },
          { key: 'responsible_name', label: 'Responsable', type: 'text' },
          { key: 'brigade_name', label: 'Brigada', type: 'text' },
          { key: 'ext_type', label: 'Tipo', type: 'text' },
          { key: 'capacity', label: 'Capacidad', type: 'text' },
          { key: 'revision_number', label: 'Revisión #', type: 'select', options: [
            { value: '', label: 'Seleccione...' },
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
            { value: '4', label: '4' },
            { value: '5', label: '5' },
            { value: '6', label: '6' },
          ] },
        ],
      },
      {
        title: 'Firmas',
        fields: [
          { key: 'performed_by_name', label: 'Responsable - Nombre', type: 'text' },
          { key: 'performed_by_signature', label: 'Responsable - Firma', type: 'signature' },
          { key: 'reviewed_by_name', label: 'Quien revisa - Nombre', type: 'text' },
          { key: 'reviewed_by_signature', label: 'Quien revisa - Firma', type: 'signature' },
        ],
      },
    ],
    sections: [
      {
        key: 'checklist',
        type: 'fixed_rows',
        title: 'Items',
        columns: [
          { key: 'status', label: 'Resultado', type: 'select', options: CONFORMITY_OPTIONS },
          { key: 'observation', label: 'Observación', type: 'text' },
        ],
        rows: rowsFromStrings(EXTINTORES_ITEMS),
      },
    ],
  },
  vehiculo: {
    title: 'Vehículo',
    description: 'Inspección de vehículo.',
    icon: Truck,
    gradient: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
    formCode: 'FT-SIG-142',
    version: '003',
    summaryColumns: [
      { key: 'inspection_date', label: 'Fecha' },
      { key: 'plate', label: 'Placa' },
      { key: 'driver_name', label: 'Conductor' },
      { key: 'project_name', label: 'Proyecto' },
    ],
    fieldGroups: [
      {
        title: 'Datos del vehículo',
        fields: [
          { key: 'inspection_date', label: 'Fecha', type: 'date', required: true, defaultValue: todayYmd() },
          { key: 'start_time', label: 'Hora inicial', type: 'time' },
          { key: 'model', label: 'Modelo', type: 'text' },
          { key: 'plate', label: 'Placa', type: 'text', required: true },
          { key: 'vehicle_class', label: 'Clase', type: 'text' },
          { key: 'color', label: 'Color', type: 'text' },
          { key: 'driver_name', label: 'Nombre del conductor', type: 'text' },
          { key: 'activity_type', label: 'Tipo de actividad', type: 'text' },
          { key: 'brigade_name', label: 'Brigada', type: 'text' },
          { key: 'project_name', label: 'Proyecto', type: 'text' },
        ],
      },
      {
        title: 'Llantas (mediciones)',
        fields: [
          { key: 'tire_inf_right_ext_pressure', label: 'Inf der ext - Presión', type: 'text' },
          { key: 'tire_inf_right_ext_wear', label: 'Inf der ext - Desgaste', type: 'text' },
          { key: 'tire_inf_right_int_pressure', label: 'Inf der int - Presión', type: 'text' },
          { key: 'tire_inf_right_int_wear', label: 'Inf der int - Desgaste', type: 'text' },
          { key: 'tire_inf_left_ext_pressure', label: 'Inf izq ext - Presión', type: 'text' },
          { key: 'tire_inf_left_ext_wear', label: 'Inf izq ext - Desgaste', type: 'text' },
          { key: 'tire_inf_left_int_pressure', label: 'Inf izq int - Presión', type: 'text' },
          { key: 'tire_inf_left_int_wear', label: 'Inf izq int - Desgaste', type: 'text' },
          { key: 'tire_sup_left_pressure', label: 'Sup izq - Presión', type: 'text' },
          { key: 'tire_sup_left_wear', label: 'Sup izq - Desgaste', type: 'text' },
          { key: 'tire_sup_right_pressure', label: 'Sup der - Presión', type: 'text' },
          { key: 'tire_sup_right_wear', label: 'Sup der - Desgaste', type: 'text' },
        ],
      },
      {
        title: 'Firmas',
        fields: [
          { key: 'conductor_cc', label: 'Conductor C.C.', type: 'text' },
          { key: 'conductor_signature', label: 'Conductor - Firma', type: 'signature' },
          { key: 'inspector_cc', label: 'Responsable inspección C.C.', type: 'text' },
          { key: 'inspector_signature', label: 'Responsable inspección - Firma', type: 'signature' },
        ],
      },
    ],
    sections: [
      {
        key: 'checklist',
        type: 'fixed_rows',
        title: 'Lista de chequeo',
        columns: [
          { key: 'status', label: 'Resultado', type: 'select', options: CONFORMITY_OPTIONS },
          { key: 'observation', label: 'Observación', type: 'text' },
        ],
        rows: rowsFromStrings(VEHICULO_ITEMS.flatMap((g) => g.items.map((x) => `${g.group} - ${x}`))),
      },
    ],
  },
  pertiga: {
    title: 'Pértiga',
    description: 'Inspección de pértiga.',
    icon: ClipboardList,
    gradient: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
    formCode: 'FT-SIG-151',
    version: '03',
    summaryColumns: [
      { key: 'inspection_date', label: 'Fecha' },
      { key: 'brigade_responsible', label: 'Responsable brigada' },
      { key: 'responsible_id', label: 'Cédula' },
      { key: 'responsible_role', label: 'Cargo' },
    ],
    fieldGroups: [
      {
        title: 'Datos',
        fields: [
          { key: 'inspection_date', label: 'Fecha', type: 'date', required: true, defaultValue: todayYmd() },
          { key: 'brigade_responsible', label: 'Responsable de la brigada', type: 'text', required: true },
          { key: 'responsible_id', label: 'Cédula', type: 'text' },
          { key: 'responsible_role', label: 'Cargo', type: 'text' },
        ],
      },
    ],
    sections: [
      {
        key: 'rows',
        type: 'dynamic_rows',
        title: 'Registro de pértigas',
        columns: [
          { key: 'dielectric_test_date', label: 'Fecha prueba dieléctrica', type: 'text' },
          { key: 'laboratory', label: 'Laboratorio', type: 'text' },
          { key: 'body_count', label: 'Número de cuerpos', type: 'text' },
          { key: 'identification_number', label: 'Número de identificación', type: 'text' },
          { key: 'physical_state', label: 'Estado físico', type: 'text' },
          { key: 'body_state', label: 'Estado del cuerpo', type: 'text' },
          { key: 'mechanical_state', label: 'Estado mecánico y acoples', type: 'text' },
          { key: 'closing_buttons_state', label: 'Botones de cierre', type: 'text' },
          { key: 'electrical_coating_state', label: 'Recubrimiento eléctrico', type: 'text' },
          { key: 'extension_stability', label: 'Estabilidad al extender', type: 'text' },
          { key: 'heads_state', label: 'Estado de cabezales', type: 'text' },
          { key: 'terminals_state', label: 'Estado de terminales', type: 'text' },
          { key: 'hook_or_hammer', label: 'Gancho o martillo', type: 'text' },
          { key: 'equipment_responsible_name', label: 'Nombre responsable equipo', type: 'text' },
          { key: 'equipment_responsible_sign', label: 'Firma responsable equipo', type: 'signature' },
        ],
        initialRows: 10,
      },
    ],
  },
  escalera: {
    title: 'Escalera',
    description: 'Inspección de escalera.',
    icon: ClipboardList,
    gradient: 'linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%)',
    formCode: 'FT-SIG-152',
    version: '2',
    summaryColumns: [
      { key: 'inspection_date', label: 'Fecha' },
      { key: 'project_name', label: 'Proyecto' },
      { key: 'ladder_type', label: 'Tipo' },
      { key: 'internal_number', label: 'Número interno' },
    ],
    fieldGroups: [
      {
        title: 'Datos de la escalera',
        fields: [
          { key: 'inspection_date', label: 'Fecha', type: 'date', required: true, defaultValue: todayYmd() },
          { key: 'project_name', label: 'Proyecto', type: 'text' },
          { key: 'ladder_type', label: 'Tipo de escalera', type: 'select', options: [
            { value: '', label: 'Seleccione...' },
            { value: 'Un cuerpo', label: 'Un cuerpo' },
            { value: 'Dos cuerpos', label: 'Dos cuerpos' },
            { value: 'Tres cuerpos', label: 'Tres cuerpos' },
          ] },
          { key: 'internal_number', label: 'Número interno', type: 'text' },
          { key: 'brand', label: 'Marca', type: 'text' },
          { key: 'series', label: 'Serie', type: 'text' },
        ],
      },
      {
        title: 'Firmas',
        fields: [
          { key: 'ladder_responsible_name', label: 'Responsable escalera - Nombre', type: 'text' },
          { key: 'ladder_responsible_role', label: 'Responsable escalera - Cargo', type: 'text' },
          { key: 'ladder_responsible_signature', label: 'Responsable escalera - Firma', type: 'signature' },
          { key: 'inspector_name', label: 'Responsable inspección - Nombre', type: 'text' },
          { key: 'inspector_role', label: 'Responsable inspección - Cargo', type: 'text' },
          { key: 'inspector_signature', label: 'Responsable inspección - Firma', type: 'signature' },
        ],
      },
    ],
    sections: [
      {
        key: 'elements',
        type: 'fixed_rows',
        title: 'Elementos para inspeccionar',
        columns: [
          { key: 'status', label: 'Estado', type: 'select', options: GOOD_BAD_NA_OPTIONS },
          { key: 'observation', label: 'Observación', type: 'text' },
        ],
        rows: rowsFromStrings(ESCALERA_ELEMENTS_ITEMS),
      },
      {
        key: 'documentation',
        type: 'fixed_rows',
        title: 'Documentación requerida',
        columns: [
          { key: 'status', label: 'Estado', type: 'select', options: YES_NO_NA_OPTIONS },
          { key: 'observation', label: 'Observación', type: 'text' },
        ],
        rows: rowsFromStrings(ESCALERA_DOC_ITEMS),
      },
    ],
  },
};

export const INSPECCIONES_SST_OPTIONS = [
  { key: 'equipos_alturas', label: 'Equipo de alturas' },
  { key: 'puesto_trabajo', label: 'Puesto de trabajo' },
  { key: 'botiquin', label: 'Botiquín' },
  { key: 'epp', label: 'EPP' },
  { key: 'ipal', label: 'IPAL' },
  { key: 'extintores', label: 'Extintores' },
  { key: 'vehiculo', label: 'Vehículo' },
  { key: 'pertiga', label: 'Pértiga' },
  { key: 'escalera', label: 'Escalera' },
];

function buildEmptySections(config) {
  const sections = {};
  (config.sections || []).forEach((section) => {
    if (section.type === 'fixed_rows') {
      const sectionValue = {};
      section.rows.forEach((row) => {
        sectionValue[row.id] = { _photos: [] };
        section.columns.forEach((col) => {
          sectionValue[row.id][col.key] = col.defaultValue || '';
        });
      });
      sections[section.key] = sectionValue;
      return;
    }
    if (section.type === 'dynamic_rows') {
      const rows = [];
      const count = section.initialRows || 1;
      for (let i = 0; i < count; i += 1) {
        const row = { _photos: [] };
        section.columns.forEach((col) => {
          row[col.key] = col.defaultValue || '';
        });
        rows.push(row);
      }
      sections[section.key] = rows;
    }
  });
  return sections;
}

function emptyForm(config) {
  const metadata = {};
  (config.fieldGroups || []).forEach((group) => {
    group.fields.forEach((field) => {
      metadata[field.key] = field.defaultValue || (field.type === 'date' ? todayYmd() : '');
    });
  });
  if (!metadata.inspection_date) metadata.inspection_date = todayYmd();
  return {
    id: '',
    metadata,
    sections: buildEmptySections(config),
    notes: '',
  };
}

function mergeSections(config, storedSections) {
  const base = buildEmptySections(config);
  const source = storedSections && typeof storedSections === 'object' ? storedSections : {};
  (config.sections || []).forEach((section) => {
    if (section.type === 'fixed_rows') {
      const sourceSection = source[section.key] && typeof source[section.key] === 'object' ? source[section.key] : {};
      section.rows.forEach((row) => {
        const sourceRow = sourceSection[row.id] && typeof sourceSection[row.id] === 'object' ? sourceSection[row.id] : {};
        section.columns.forEach((col) => {
          base[section.key][row.id][col.key] = sourceRow[col.key] || '';
        });
        base[section.key][row.id]._photos = Array.isArray(sourceRow._photos) ? sourceRow._photos : [];
      });
      return;
    }
    if (section.type === 'dynamic_rows') {
      const sourceRows = Array.isArray(source[section.key]) ? source[section.key] : [];
      base[section.key] = sourceRows.length > 0 ? sourceRows.map((item) => {
        const row = { _photos: Array.isArray(item?._photos) ? item._photos : [] };
        section.columns.forEach((col) => {
          row[col.key] = item?.[col.key] || '';
        });
        return row;
      }) : base[section.key];
    }
  });
  return base;
}

export default function InspeccionesSSTFormulariosModule({ location, inspectionType = 'equipos_alturas' }) {
  const locationLabel = useMemo(() => {
    if (location === 'popayan') return 'Popayán';
    if (location === 'santander-de-quilichao') return 'Santander de Quilichao';
    return location || 'Sede';
  }, [location]);

  const config = useMemo(() => INSPECCIONES_SST_META[inspectionType] || INSPECCIONES_SST_META.equipos_alturas, [inspectionType]);
  const Icon = config.icon;

  const [form, setForm] = useState(() => emptyForm(config));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [feedbackTone, setFeedbackTone] = useState('success');
  const [pdfExportingId, setPdfExportingId] = useState(null);

  const loadRows = async () => {
    try {
      setLoading(true);
      const res = await api.sgi.sstInspections.list({ location, inspection_type: inspectionType, limit: 1000, order: 'desc' });
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list.map((row) => ({
        ...row,
        metadata: parseJsonObject(row.metadata_json),
        sections: parseJsonObject(row.sections_json),
      })));
    } catch (err) {
      console.error('Error cargando inspecciones SST', err);
      setFeedbackTone('error');
      setFeedback('No se pudieron cargar los registros de inspecciones SST.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setForm(emptyForm(config));
    setFeedback('');
    loadRows();
  }, [config, location, inspectionType]); // eslint-disable-line react-hooks/exhaustive-deps

  const onMetadataChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [key]: value,
      },
    }));
  };

  const onFixedCellChange = (sectionKey, rowId, colKey, value) => {
    setForm((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...(prev.sections[sectionKey] || {}),
          [rowId]: {
            ...((prev.sections[sectionKey] || {})[rowId] || {}),
            [colKey]: value,
          },
        },
      },
    }));
  };

  const onDynamicCellChange = (sectionKey, rowIndex, colKey, value) => {
    setForm((prev) => {
      const currentRows = Array.isArray(prev.sections[sectionKey]) ? [...prev.sections[sectionKey]] : [];
      currentRows[rowIndex] = {
        ...(currentRows[rowIndex] || {}),
        [colKey]: value,
      };
      return {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionKey]: currentRows,
        },
      };
    });
  };

  const addDynamicRow = (section) => {
    setForm((prev) => {
      const currentRows = Array.isArray(prev.sections[section.key]) ? [...prev.sections[section.key]] : [];
      const newRow = {};
      section.columns.forEach((col) => {
        newRow[col.key] = col.defaultValue || '';
      });
      currentRows.push(newRow);
      return {
        ...prev,
        sections: {
          ...prev.sections,
          [section.key]: currentRows,
        },
      };
    });
  };

  const removeDynamicRow = (sectionKey, rowIndex) => {
    setForm((prev) => {
      const currentRows = Array.isArray(prev.sections[sectionKey]) ? [...prev.sections[sectionKey]] : [];
      currentRows.splice(rowIndex, 1);
      return {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionKey]: currentRows.length > 0 ? currentRows : [{}],
        },
      };
    });
  };

  const addFixedRowPhoto = (sectionKey, rowId, dataUrl) => {
    setForm((prev) => {
      const section = prev.sections[sectionKey] || {};
      const row = section[rowId] || {};
      const photos = Array.isArray(row._photos) ? row._photos : [];
      if (photos.length >= 3) return prev;
      return {
        ...prev,
        sections: { ...prev.sections, [sectionKey]: { ...section, [rowId]: { ...row, _photos: [...photos, dataUrl] } } },
      };
    });
  };

  const removeFixedRowPhoto = (sectionKey, rowId, idx) => {
    setForm((prev) => {
      const section = prev.sections[sectionKey] || {};
      const row = section[rowId] || {};
      const photos = Array.isArray(row._photos) ? row._photos : [];
      return {
        ...prev,
        sections: { ...prev.sections, [sectionKey]: { ...section, [rowId]: { ...row, _photos: photos.filter((_, i) => i !== idx) } } },
      };
    });
  };

  const addDynamicRowPhoto = (sectionKey, rowIndex, dataUrl) => {
    setForm((prev) => {
      const currentRows = Array.isArray(prev.sections[sectionKey]) ? [...prev.sections[sectionKey]] : [];
      const row = currentRows[rowIndex] || {};
      const photos = Array.isArray(row._photos) ? row._photos : [];
      if (photos.length >= 3) return prev;
      currentRows[rowIndex] = { ...row, _photos: [...photos, dataUrl] };
      return { ...prev, sections: { ...prev.sections, [sectionKey]: currentRows } };
    });
  };

  const removeDynamicRowPhoto = (sectionKey, rowIndex, idx) => {
    setForm((prev) => {
      const currentRows = Array.isArray(prev.sections[sectionKey]) ? [...prev.sections[sectionKey]] : [];
      const row = currentRows[rowIndex] || {};
      const photos = Array.isArray(row._photos) ? row._photos : [];
      currentRows[rowIndex] = { ...row, _photos: photos.filter((_, i) => i !== idx) };
      return { ...prev, sections: { ...prev.sections, [sectionKey]: currentRows } };
    });
  };

  const [gpsStatus, setGpsStatus] = useState('');

  const captureGps = () => {
    if (!navigator.geolocation) {
      setGpsStatus('La geolocalización no está disponible en este dispositivo.');
      return;
    }
    setGpsStatus('Obteniendo ubicación...');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        onMetadataChange('gps_lat', String(latitude));
        onMetadataChange('gps_lng', String(longitude));
        onMetadataChange('gps_captured_at', new Date().toISOString());
        setGpsStatus('');
      },
      (err) => {
        console.error('Error obteniendo ubicación', err);
        setGpsStatus('No se pudo obtener la ubicación. Verifica los permisos del navegador.');
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const clearGps = () => {
    onMetadataChange('gps_lat', '');
    onMetadataChange('gps_lng', '');
    onMetadataChange('gps_captured_at', '');
    setGpsStatus('');
  };

  // Considera si hay algo realmente diligenciado (fuera de la fecha, que
  // siempre trae un valor por defecto) para no permitir guardar en silencio
  // un formulario que en la práctica está vacío.
  const formHasRealContent = () => {
    const hasMetadata = (config.fieldGroups || [])
      .flatMap((group) => group.fields)
      .some((field) => field.key !== 'inspection_date' && String(form.metadata[field.key] || '').trim() !== '');
    if (hasMetadata) return true;
    if (String(form.notes || '').trim() !== '') return true;
    return (config.sections || []).some((section) => {
      if (section.type === 'fixed_rows') {
        return section.rows.some((row) => {
          const rowData = form.sections?.[section.key]?.[row.id] || {};
          const filled = section.columns.some((col) => String(rowData[col.key] || '').trim() !== '');
          return filled || (Array.isArray(rowData._photos) && rowData._photos.length > 0);
        });
      }
      return (form.sections?.[section.key] || []).some((row) => {
        const filled = section.columns.some((col) => String(row?.[col.key] || '').trim() !== '');
        return filled || (Array.isArray(row?._photos) && row._photos.length > 0);
      });
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const requiredField = (config.fieldGroups || [])
      .flatMap((group) => group.fields)
      .find((field) => field.required && !String(form.metadata[field.key] || '').trim());

    if (requiredField) {
      setFeedbackTone('error');
      setFeedback(`Completa el campo obligatorio: ${requiredField.label}.`);
      return;
    }

    if (!formHasRealContent()) {
      const confirmEmpty = window.confirm('No se ha diligenciado ningún dato ni ítem del checklist. ¿Seguro que quieres guardar este formulario vacío?');
      if (!confirmEmpty) return;
    }

    try {
      setSaving(true);
      setFeedback('');
      const res = await api.sgi.sstInspections.upsert({
        id: form.id || '',
        location,
        inspection_type: inspectionType,
        inspection_date: form.metadata.inspection_date || '',
        metadata_json: form.metadata,
        sections_json: form.sections,
        notes: form.notes || '',
      });
      setGpsStatus('');
      setFeedbackTone('success');
      if (res?.queued) {
        setFeedback(`Guardado en este dispositivo. Se sincronizará con el servidor cuando haya conexión (${config.title}).`);
      } else {
        if (res?.id && !form.id) {
          setForm((prev) => ({ ...prev, id: String(res.id) }));
        }
        setFeedback(`Registro #${res?.id || form.id} guardado correctamente en ${config.title}. El formulario NO se borró: usa "Limpiar" si vas a registrar una inspección nueva.`);
      }
      await loadRows();
    } catch (err) {
      console.error('Error guardando inspección SST', err);
      setFeedbackTone('error');
      setFeedback(err?.message || 'No se pudo guardar el registro.');
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (row) => {
    const metadata = { ...emptyForm(config).metadata, ...parseJsonObject(row.metadata_json || row.metadata) };
    const sections = mergeSections(config, parseJsonObject(row.sections_json || row.sections));
    setForm({
      id: row.id || '',
      metadata,
      sections,
      notes: row.notes || '',
    });
    setFeedbackTone('success');
    setFeedback(`Editando registro #${row.id} de ${config.title}.`);
  };

  const onDelete = async (row) => {
    const ok = window.confirm(`¿Eliminar el registro #${row.id} de ${config.title}?`);
    if (!ok) return;
    try {
      await api.sgi.sstInspections.delete({ id: row.id });
      if (String(form.id) === String(row.id)) setForm(emptyForm(config));
      setFeedbackTone('success');
      setFeedback('Registro eliminado.');
      await loadRows();
    } catch (err) {
      console.error('Error eliminando inspección SST', err);
      setFeedbackTone('error');
      setFeedback(err?.message || 'No se pudo eliminar el registro.');
    }
  };

  const exportExcel = async (selectedRows = rows) => {
    try {
      const logoDataUrl = await assetToDataUrl(senerfiFormato);
      const headerTitle = config.title.toUpperCase();
      const sheetName = escapeHtml((config.title || 'Inspecciones SST').slice(0, 31));

      const formatFieldValue = (field, value) => {
        if (!value) return '';
        if (field?.type === 'signature') return String(value).startsWith('data:image') ? 'Firmado' : String(value);
        if (field?.type === 'date') return fmtDateDisplay(value);
        return String(value);
      };

      const buildMetaRows = (metadata) => {
        const fields = (config.fieldGroups || []).flatMap((group) => group.fields || []);
        const pairs = fields.map((field) => ({
          label: field.label,
          value: formatFieldValue(field, metadata?.[field.key] || ''),
        }));
        const rowsHtml = [];
        for (let i = 0; i < pairs.length; i += 2) {
          const left = pairs[i];
          const right = pairs[i + 1];
          rowsHtml.push(`
            <tr>
              <td class="meta-label">${escapeHtml(left?.label || '')}</td>
              <td class="meta-value">${escapeHtml(left?.value || '')}</td>
              <td class="meta-label">${escapeHtml(right?.label || '')}</td>
              <td class="meta-value">${escapeHtml(right?.value || '')}</td>
            </tr>
          `);
        }
        return rowsHtml.join('');
      };

      const normalizeSectionData = (section, sectionsData) => {
        const sectionData = sectionsData?.[section.key];
        if (section.type === 'fixed_rows') {
          return section.rows.map((row) => {
            const rowData = (sectionData && typeof sectionData === 'object' ? sectionData[row.id] : {}) || {};
            return { label: row.label, data: rowData };
          });
        }
        const dynamicRows = Array.isArray(sectionData) ? sectionData : [];
        const nonEmpty = dynamicRows.filter((row) => {
          if (!row || typeof row !== 'object') return false;
          return section.columns.some((col) => String(row[col.key] || '').trim() !== '');
        });
        return nonEmpty;
      };

      const buildSectionTable = (section, sectionsData) => {
        if (section.type === 'fixed_rows') {
          const fixedRows = normalizeSectionData(section, sectionsData);
          const body = fixedRows.map((r, idx) => `
            <tr>
              <td class="center">${escapeHtml(idx + 1)}</td>
              <td>${escapeHtml(r.label)}</td>
              ${section.columns.map((col) => `<td>${escapeHtml(formatFieldValue(col, r.data?.[col.key] || ''))}</td>`).join('')}
            </tr>
          `).join('');
          return `
            <table>
              <tr><th class="section-cell" colspan="${2 + section.columns.length}">${escapeHtml(section.title.toUpperCase())}</th></tr>
              <tr>
                <th class="header w-no">#</th>
                <th class="header w-label">ITEM</th>
                ${section.columns.map((col) => `<th class="header">${escapeHtml(col.label)}</th>`).join('')}
              </tr>
              ${body}
            </table>
          `;
        }

        const dynamicRows = normalizeSectionData(section, sectionsData);
        const body = dynamicRows.map((r, idx) => `
          <tr>
            <td class="center">${escapeHtml(idx + 1)}</td>
            ${section.columns.map((col) => `<td>${escapeHtml(formatFieldValue(col, r?.[col.key] || ''))}</td>`).join('')}
          </tr>
        `).join('');
        return `
          <table>
            <tr><th class="section-cell" colspan="${1 + section.columns.length}">${escapeHtml(section.title.toUpperCase())}</th></tr>
            <tr>
              <th class="header w-no">#</th>
              ${section.columns.map((col) => `<th class="header">${escapeHtml(col.label)}</th>`).join('')}
            </tr>
            ${body || `<tr><td colspan="${1 + section.columns.length}" class="center">Sin registros</td></tr>`}
          </table>
        `;
      };

      const documentsHtml = (selectedRows || []).map((row) => {
        const metadata = parseJsonObject(row.metadata_json || row.metadata);
        const sectionsData = parseJsonObject(row.sections_json || row.sections);
        const inspectionDate = metadata?.inspection_date || row.inspection_date || '';
        const metaTable = `
          <table>
            <tr>
              <td class="meta-label">SEDE</td>
              <td class="meta-value">${escapeHtml(locationLabel)}</td>
              <td class="meta-label">REGISTRO</td>
              <td class="meta-value">#${escapeHtml(row.id || '')}</td>
            </tr>
            ${buildMetaRows(metadata)}
          </table>
        `;
        const sectionsTables = (config.sections || []).map((section) => buildSectionTable(section, sectionsData)).join('');
        return `
          <div class="page">
            <table class="header-table">
              <tr>
                <td class="logo-cell" colspan="1" rowspan="3"><img src="${logoDataUrl}" alt="Senerfi" /></td>
                <td class="title" colspan="4">SOLUCIONES ENERGETICAS FORESTALES INTEGRALES SAS</td>
                <td class="meta-label">CODIGO:</td>
                <td class="meta-value">${escapeHtml(config.formCode)}</td>
              </tr>
              <tr>
                <td class="title" colspan="4">SISTEMA DE GESTION DE SEGURIDAD Y SALUD EN EL TRABAJO</td>
                <td class="meta-label">VERSION:</td>
                <td class="meta-value">${escapeHtml(config.version)}</td>
              </tr>
              <tr>
                <td class="title" colspan="4">${escapeHtml(headerTitle)}</td>
                <td class="meta-label">FECHA:</td>
                <td class="meta-value">${escapeHtml(fmtDateDisplay(inspectionDate || todayYmd()))}</td>
              </tr>
            </table>
            ${metaTable}
            ${sectionsTables}
            <table>
              <tr>
                <td class="meta-label">OBSERVACIONES GENERALES</td>
                <td class="meta-value" colspan="5">${escapeHtml(row.notes || '')}</td>
              </tr>
            </table>
          </div>
        `;
      }).join('');

      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="UTF-8" />
            <!--[if gte mso 9]><xml>
              <x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
                <x:Name>${sheetName}</x:Name>
                <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
              </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook>
            </xml><![endif]-->
            <style>
              body { font-family: Arial, sans-serif; }
              table { border-collapse: collapse; width: 100%; table-layout: fixed; margin-bottom: 12px; }
              td, th { border: 1px solid #000; padding: 6px 8px; font-size: 10pt; vertical-align: middle; word-wrap: break-word; }
              .title { text-align: center; font-weight: 700; }
              .meta-label { font-weight: 700; text-align: left; width: 180px; }
              .meta-value { text-align: left; }
              .header { background: #f4bf16; font-weight: 700; text-align: center; }
              .section-cell { background: #f3f4f6; font-weight: 700; text-align: left; }
              .logo-cell { text-align: center; }
              .logo-cell img { max-width: 180px; max-height: 70px; }
              .center { text-align: center; }
              .page { page-break-after: always; margin-bottom: 24px; }
              .w-no { width: 60px; }
              .w-label { width: 380px; }
              .header-table td { background: #fff; }
            </style>
          </head>
          <body>
            ${documentsHtml || '<table><tr><td>Sin registros para exportar</td></tr></table>'}
          </body>
        </html>
      `;

      const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `inspeccion_sst_${safeSlug(inspectionType)}_${safeSlug(location || 'sede')}_${todayYmd()}.xls`;
      document.body.appendChild(link);
      void recordOperationsDownload({ fileType: 'xls', moduleKey: 'sgi', reportName: 'Inspección SST' });
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando inspección SST', err);
      setFeedbackTone('error');
      setFeedback('No se pudo exportar el archivo Excel.');
    }
  };

  const exportPdf = async (row) => {
    let frame = null;
    try {
      setPdfExportingId(row.id);
      const logoDataUrl = await assetToDataUrl(senerfiFormato);
      const metadata = parseJsonObject(row.metadata_json || row.metadata);
      const sectionsData = parseJsonObject(row.sections_json || row.sections);
      const inspectionDate = metadata?.inspection_date || row.inspection_date || '';

      const isImageValue = (v) => typeof v === 'string' && v.startsWith('data:image');

      const formatMetaValue = (field, value) => {
        if (!value) return '';
        if (field.type === 'signature') {
          return isImageValue(value) ? `<img src="${value}" style="height:34px;" alt="Firma" />` : escapeHtml(String(value));
        }
        if (field.type === 'date') return escapeHtml(fmtDateDisplay(value));
        return escapeHtml(String(value));
      };

      const cellValueHtml = (col, value) => {
        if (col.type === 'signature') {
          return isImageValue(value) ? `<img src="${value}" style="height:30px;" alt="Firma" />` : escapeHtml(String(value || ''));
        }
        return escapeHtml(String(value || ''));
      };

      const photosHtml = (photos) => {
        const list = Array.isArray(photos) ? photos.filter((p) => isImageValue(p)) : [];
        if (!list.length) return '';
        return `<div class="photo-row">${list.map((src) => `<img src="${src}" class="photo-thumb" alt="Foto" />`).join('')}</div>`;
      };

      const metaFields = (config.fieldGroups || []).flatMap((group) => group.fields || []);
      let metaRowsHtml = '';
      for (let i = 0; i < metaFields.length; i += 2) {
        const left = metaFields[i];
        const right = metaFields[i + 1];
        metaRowsHtml += `
          <tr>
            <td class="meta-label">${escapeHtml(left?.label || '')}</td>
            <td class="meta-value">${left ? formatMetaValue(left, metadata?.[left.key] || '') : ''}</td>
            <td class="meta-label">${escapeHtml(right?.label || '')}</td>
            <td class="meta-value">${right ? formatMetaValue(right, metadata?.[right.key] || '') : ''}</td>
          </tr>
        `;
      }

      const sectionsHtml = (config.sections || []).map((section) => {
        if (section.type === 'fixed_rows') {
          const sectionData = (sectionsData && typeof sectionsData === 'object' ? sectionsData[section.key] : {}) || {};
          const bodyRows = section.rows.map((r, idx) => {
            const rowData = (sectionData[r.id] && typeof sectionData[r.id] === 'object') ? sectionData[r.id] : {};
            const photosRow = Array.isArray(rowData._photos) && rowData._photos.length
              ? `<tr><td></td><td colspan="${section.columns.length + 1}">${photosHtml(rowData._photos)}</td></tr>`
              : '';
            return `
              <tr>
                <td class="center">${idx + 1}</td>
                <td>${escapeHtml(r.label)}</td>
                ${section.columns.map((col) => `<td>${cellValueHtml(col, rowData[col.key])}</td>`).join('')}
              </tr>
              ${photosRow}
            `;
          }).join('');
          return `
            <table>
              <tr><th class="section-cell" colspan="${2 + section.columns.length}">${escapeHtml(section.title.toUpperCase())}</th></tr>
              <tr>
                <th class="header w-no">#</th>
                <th class="header w-label">ITEM</th>
                ${section.columns.map((col) => `<th class="header">${escapeHtml(col.label)}</th>`).join('')}
              </tr>
              ${bodyRows}
            </table>
          `;
        }

        const rowsArr = Array.isArray(sectionsData?.[section.key]) ? sectionsData[section.key] : [];
        const nonEmpty = rowsArr.filter((r) => r && typeof r === 'object' && section.columns.some((col) => String(r[col.key] || '').trim() !== ''));
        const bodyRows = nonEmpty.map((r, idx) => {
          const photosRow = Array.isArray(r._photos) && r._photos.length
            ? `<tr><td></td><td colspan="${section.columns.length}">${photosHtml(r._photos)}</td></tr>`
            : '';
          return `
            <tr>
              <td class="center">${idx + 1}</td>
              ${section.columns.map((col) => `<td>${cellValueHtml(col, r[col.key])}</td>`).join('')}
            </tr>
            ${photosRow}
          `;
        }).join('');
        return `
          <table>
            <tr><th class="section-cell" colspan="${1 + section.columns.length}">${escapeHtml(section.title.toUpperCase())}</th></tr>
            <tr>
              <th class="header w-no">#</th>
              ${section.columns.map((col) => `<th class="header">${escapeHtml(col.label)}</th>`).join('')}
            </tr>
            ${bodyRows || `<tr><td colspan="${1 + section.columns.length}" class="center">Sin registros</td></tr>`}
          </table>
        `;
      }).join('');

      const html = `
        <!doctype html>
        <html>
        <head>
          <meta charset="UTF-8" />
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 12px; background: #fff; color: #111; }
            .sheet { width: 1000px; }
            table { border-collapse: collapse; width: 100%; table-layout: fixed; margin-bottom: 10px; }
            td, th { border: 1px solid #000; padding: 5px 7px; font-size: 11px; vertical-align: middle; word-wrap: break-word; }
            .title { text-align: center; font-weight: 700; }
            .meta-label { font-weight: 700; text-align: left; width: 160px; }
            .meta-value { text-align: left; }
            .header { background: #f4bf16; font-weight: 700; text-align: center; }
            .section-cell { background: #f3f4f6; font-weight: 700; text-align: left; }
            .logo-cell { text-align: center; }
            .logo-cell img { max-width: 150px; max-height: 60px; }
            .center { text-align: center; }
            .w-no { width: 50px; }
            .w-label { width: 320px; }
            .photo-row { display: flex; gap: 6px; flex-wrap: wrap; padding: 4px 0; }
            .photo-thumb { width: 70px; height: 70px; object-fit: cover; border: 1px solid #ccc; border-radius: 4px; }
          </style>
        </head>
        <body>
          <div class="sheet">
            <table>
              <tr>
                <td class="logo-cell" rowspan="3"><img src="${logoDataUrl}" alt="Senerfi" /></td>
                <td class="title" colspan="4">SOLUCIONES ENERGETICAS FORESTALES INTEGRALES SAS</td>
                <td class="meta-label">CODIGO:</td>
                <td class="meta-value">${escapeHtml(config.formCode)}</td>
              </tr>
              <tr>
                <td class="title" colspan="4">SISTEMA DE GESTION DE SEGURIDAD Y SALUD EN EL TRABAJO</td>
                <td class="meta-label">VERSION:</td>
                <td class="meta-value">${escapeHtml(config.version)}</td>
              </tr>
              <tr>
                <td class="title" colspan="4">${escapeHtml(config.title.toUpperCase())}</td>
                <td class="meta-label">FECHA:</td>
                <td class="meta-value">${escapeHtml(fmtDateDisplay(inspectionDate || todayYmd()))}</td>
              </tr>
            </table>
            <table>
              <tr>
                <td class="meta-label">SEDE</td>
                <td class="meta-value">${escapeHtml(locationLabel)}</td>
                <td class="meta-label">REGISTRO</td>
                <td class="meta-value">#${escapeHtml(row.id || '')}</td>
              </tr>
              ${metaRowsHtml}
            </table>
            ${sectionsHtml}
            <table>
              <tr>
                <td class="meta-label">OBSERVACIONES GENERALES</td>
                <td class="meta-value" colspan="5">${escapeHtml(row.notes || '')}</td>
              </tr>
            </table>
          </div>
        </body>
        </html>
      `;

      frame = document.createElement('iframe');
      frame.style.position = 'fixed';
      frame.style.left = '-10000px';
      frame.style.top = '0';
      frame.style.width = '1050px';
      frame.style.height = '1500px';
      frame.style.border = '0';
      document.body.appendChild(frame);
      const doc = frame.contentDocument || frame.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();

      const imgs = Array.from(doc.images || []);
      await Promise.all(imgs.map((img) => (
        img.complete ? Promise.resolve() : new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; })
      )));
      await new Promise((r) => setTimeout(r, 60));

      const el = doc.querySelector('.sheet');
      const ww = Math.max(1000, Number(el?.scrollWidth || 0) || 0);
      const wh = Math.max(1000, Number(el?.scrollHeight || 0) || 0);
      const renderScale = 2;
      const canvas = await html2canvas(el, {
        scale: renderScale,
        backgroundColor: '#FFFFFF',
        useCORS: true,
        windowWidth: ww,
        windowHeight: wh,
      });

      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 6;
      const pxToMm = 0.26458333333333334;
      const mmW = (canvas.width / renderScale) * pxToMm;
      const mmH = (canvas.height / renderScale) * pxToMm;
      const maxW = pageW - (margin * 2);
      const pageInnerH = pageH - (margin * 2);
      const fitScale = Math.min(1, maxW / mmW);
      const drawW = mmW * fitScale;
      const drawH = mmH * fitScale;

      let offsetY = 0;
      pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
      while (offsetY + pageInnerH < drawH) {
        offsetY += pageInnerH;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
      }

      void recordOperationsDownload({ fileType: 'pdf', moduleKey: 'sgi', reportName: 'Inspección SST' });
      pdf.save(`inspeccion_sst_${safeSlug(inspectionType)}_${row.id || ''}_${todayYmd()}.pdf`);
    } catch (err) {
      console.error('Error exportando PDF de inspección SST', err);
      setFeedbackTone('error');
      setFeedback('No se pudo generar el PDF de la inspección.');
    } finally {
      if (frame) { try { frame.remove(); } catch (_) {} }
      setPdfExportingId(null);
    }
  };

  const renderField = (field) => {
    if (field.type === 'signature') {
      return (
        <SignatureField
          value={form.metadata[field.key] || ''}
          onChange={(dataUrl) => onMetadataChange(field.key, dataUrl)}
        />
      );
    }
    if (field.type === 'textarea') {
      return (
        <textarea
          className="w-full border rounded px-3 py-2 min-h-[90px]"
          value={form.metadata[field.key] || ''}
          onChange={(e) => onMetadataChange(field.key, e.target.value)}
        />
      );
    }
    if (field.type === 'select') {
      return (
        <select
          className="w-full border rounded px-3 py-2 bg-white"
          value={form.metadata[field.key] || ''}
          onChange={(e) => onMetadataChange(field.key, e.target.value)}
        >
          {(field.options || []).map((option) => (
            <option key={option.value || 'empty'} value={option.value}>{option.label}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        type={field.type || 'text'}
        className="w-full border rounded px-3 py-2"
        value={form.metadata[field.key] || ''}
        onChange={(e) => onMetadataChange(field.key, e.target.value)}
      />
    );
  };

  const renderCell = (value, col, onChange) => {
    if (col.type === 'signature') {
      return <SignatureField value={value || ''} onChange={onChange} />;
    }
    if (col.type === 'select') {
      return (
        <select className="w-full border rounded px-2 py-1 bg-white" value={value || ''} onChange={(e) => onChange(e.target.value)}>
          {(col.options || []).map((option) => (
            <option key={option.value || 'empty'} value={option.value}>{option.label}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        type={col.type || 'text'}
        className="w-full border rounded px-2 py-1"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-sm" style={{ background: config.gradient }}>
            <Icon size={20} className="text-white" />
          </div>
          <div>
            <div className="text-base font-semibold text-gray-800">{config.title}</div>
            <div className="text-sm text-gray-600">{config.description}</div>
            <div className="text-xs text-gray-500 mt-1">Sede: {locationLabel} | Código: {config.formCode} | Versión: {config.version}</div>
          </div>
        </div>
        {!IS_SIG_MOBILE && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => exportExcel(rows)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white border shadow-sm hover:brightness-110"
              style={{ background: config.gradient, borderColor: '#0f766e' }}
            >
              <Download size={16} /> Exportar Excel
            </button>
          </div>
        )}
      </div>

      <form onSubmit={onSubmit} className="bg-white border border-gray-200 rounded-xl p-4 space-y-5">
        <div className="flex flex-wrap items-center gap-2 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <button
            type="button"
            onClick={captureGps}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-white"
          >
            <MapPin size={14} /> Obtener ubicación
          </button>
          {form.metadata.gps_lat && form.metadata.gps_lng ? (
            <>
              <span className="text-emerald-700">
                Ubicación registrada: {Number(form.metadata.gps_lat).toFixed(5)}, {Number(form.metadata.gps_lng).toFixed(5)}
                {form.metadata.gps_captured_at ? ` (${new Date(form.metadata.gps_captured_at).toLocaleString('es-CO')})` : ''}
              </span>
              <button type="button" onClick={clearGps} className="text-red-600 hover:underline">Borrar ubicación</button>
            </>
          ) : gpsStatus ? (
            <span className="text-gray-500">{gpsStatus}</span>
          ) : (
            <span className="text-gray-400">Sin ubicación registrada.</span>
          )}
        </div>

        {(config.fieldGroups || []).map((group) => (
          <div key={group.title} className="space-y-3">
            <div className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">{group.title}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {group.fields.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium mb-1">{field.label}{field.required ? ' *' : ''}</label>
                  {renderField(field)}
                </div>
              ))}
            </div>
          </div>
        ))}

        {(config.sections || []).map((section) => {
          const selectCols = section.columns.filter((col) => col.type === 'select');
          const isRowFilled = (rowData) => selectCols.some((col) => String(rowData?.[col.key] || '').trim() !== '');
          const filledCount = section.type === 'fixed_rows'
            ? section.rows.filter((row) => isRowFilled(form.sections?.[section.key]?.[row.id])).length
            : 0;

          return (
            <div key={section.key} className="space-y-3">
              <div className="flex items-center justify-between gap-3 border-b border-gray-200 pb-2">
                <div>
                  <div className="text-sm font-semibold text-gray-800">{section.title}</div>
                  {section.type === 'fixed_rows' && selectCols.length > 0 ? (
                    <div className="text-xs text-gray-500">{filledCount} de {section.rows.length} ítems completados</div>
                  ) : null}
                </div>
                {section.type === 'dynamic_rows' ? (
                  <button
                    type="button"
                    onClick={() => addDynamicRow(section)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs"
                  >
                    <Plus size={14} /> Agregar fila
                  </button>
                ) : null}
              </div>

              {/* Vista de tarjetas: pantallas angostas (móvil) */}
              <div className="grid grid-cols-1 gap-3 md:hidden">
                {section.type === 'fixed_rows' && section.rows.map((row, index) => (
                  <div key={row.id} className="border rounded-xl p-3 bg-gray-50 space-y-2">
                    <div className="text-sm font-medium text-gray-800">{index + 1}. {row.label}</div>
                    {section.columns.map((col) => (
                      <div key={col.key}>
                        <label className="block text-xs text-gray-500 mb-1">{col.label}</label>
                        {renderCell(form.sections?.[section.key]?.[row.id]?.[col.key] || '', col, (value) => onFixedCellChange(section.key, row.id, col.key, value))}
                      </div>
                    ))}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Fotos (máx. 3)</label>
                      <PhotoPicker
                        photos={form.sections?.[section.key]?.[row.id]?._photos || []}
                        onAdd={(dataUrl) => addFixedRowPhoto(section.key, row.id, dataUrl)}
                        onRemove={(idx) => removeFixedRowPhoto(section.key, row.id, idx)}
                      />
                    </div>
                  </div>
                ))}
                {section.type === 'dynamic_rows' && (form.sections?.[section.key] || []).map((row, rowIndex) => (
                  <div key={`${section.key}-card-${rowIndex}`} className="border rounded-xl p-3 bg-gray-50 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-800">Fila {rowIndex + 1}</div>
                      <button
                        type="button"
                        onClick={() => removeDynamicRow(section.key, rowIndex)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50 text-xs"
                      >
                        <Trash2 size={12} /> Quitar
                      </button>
                    </div>
                    {section.columns.map((col) => (
                      <div key={col.key}>
                        <label className="block text-xs text-gray-500 mb-1">{col.label}</label>
                        {renderCell(row?.[col.key] || '', col, (value) => onDynamicCellChange(section.key, rowIndex, col.key, value))}
                      </div>
                    ))}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Fotos (máx. 3)</label>
                      <PhotoPicker
                        photos={row?._photos || []}
                        onAdd={(dataUrl) => addDynamicRowPhoto(section.key, rowIndex, dataUrl)}
                        onRemove={(idx) => removeDynamicRowPhoto(section.key, rowIndex, idx)}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Vista de tabla: pantallas medianas y grandes (escritorio) */}
              <div className="hidden md:block overflow-x-auto border rounded-xl">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left">{section.type === 'dynamic_rows' ? '#' : 'Ítem'}</th>
                      {section.type === 'dynamic_rows' ? null : <th className="px-3 py-2 text-left">Detalle</th>}
                      {section.columns.map((col) => (
                        <th key={col.key} className="px-3 py-2 text-left">{col.label}</th>
                      ))}
                      {section.type === 'dynamic_rows' ? <th className="px-3 py-2 text-left">Acción</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {section.type === 'fixed_rows' && section.rows.map((row, index) => (
                      <tr key={row.id} className="border-t align-top">
                        <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                        <td className="px-3 py-2 min-w-[320px]">{row.label}</td>
                        {section.columns.map((col) => (
                          <td key={col.key} className="px-3 py-2 min-w-[150px]">
                            {renderCell(form.sections?.[section.key]?.[row.id]?.[col.key] || '', col, (value) => onFixedCellChange(section.key, row.id, col.key, value))}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {section.type === 'dynamic_rows' && (form.sections?.[section.key] || []).map((row, rowIndex) => (
                      <tr key={`${section.key}-${rowIndex}`} className="border-t align-top">
                        <td className="px-3 py-2 text-gray-500">{rowIndex + 1}</td>
                        {section.columns.map((col) => (
                          <td key={col.key} className="px-3 py-2 min-w-[170px]">
                            {renderCell(row?.[col.key] || '', col, (value) => onDynamicCellChange(section.key, rowIndex, col.key, value))}
                          </td>
                        ))}
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => removeDynamicRow(section.key, rowIndex)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={14} /> Quitar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

        <div>
          <label className="block text-sm font-medium mb-1">Observaciones generales</label>
          <textarea
            className="w-full border rounded px-3 py-2 min-h-[90px]"
            value={form.notes}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
          />
        </div>

        {feedback ? (
          <div className={
            feedbackTone === 'error'
              ? 'text-sm rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2'
              : 'text-sm rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 px-3 py-2'
          }>{feedback}</div>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              if (formHasRealContent()) {
                const confirmClear = window.confirm('Vas a borrar todo lo que llevas diligenciado en este formulario (no se ha guardado). ¿Seguro que quieres limpiarlo?');
                if (!confirmClear) return;
              }
              setForm(emptyForm(config));
              setFeedback('');
            }}
            className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 border border-gray-300"
          >
            Limpiar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white border shadow-sm hover:brightness-110 disabled:opacity-60"
            style={{ background: config.gradient, borderColor: '#059669' }}
          >
            <Save size={16} /> {saving ? 'Guardando...' : 'Guardar inspección'}
          </button>
        </div>
      </form>

      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div>
          <div className="text-sm font-semibold text-gray-800">Registros guardados</div>
          <div className="text-xs text-gray-500">{loading ? 'Cargando...' : `${rows.length} registros en ${config.title}`}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="px-3 py-2 text-left">ID</th>
                {config.summaryColumns.map((col) => (
                  <th key={col.key} className="px-3 py-2 text-left">{col.label}</th>
                ))}
                <th className="px-3 py-2 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const metadata = parseJsonObject(row.metadata_json || row.metadata);
                return (
                  <tr key={row.id} className="border-t">
                    <td className="px-3 py-2">
                      {row.id}
                      {row.offline_queued ? (
                        <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-[11px] font-medium align-middle">
                          Pendiente de sincronizar
                        </span>
                      ) : null}
                    </td>
                    {config.summaryColumns.map((col) => (
                      <td key={col.key} className="px-3 py-2">
                        {col.key === 'inspection_date'
                          ? fmtDateDisplay(metadata[col.key] || row.inspection_date || '')
                          : (metadata[col.key] || '-')}
                      </td>
                    ))}
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {!IS_SIG_MOBILE && (
                          <button
                            type="button"
                            onClick={() => exportExcel([row])}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                          >
                            <Download size={14} /> Exportar
                          </button>
                        )}
                        {!IS_SIG_MOBILE && (
                          <button
                            type="button"
                            onClick={() => exportPdf(row)}
                            disabled={pdfExportingId === row.id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-60"
                          >
                            <FileText size={14} /> {pdfExportingId === row.id ? 'Generando...' : 'PDF'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => onEdit(row)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
                        >
                          <Pencil size={14} /> Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(row)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={14} /> Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 ? (
                <tr>
                  <td className="px-3 py-4 text-gray-500" colSpan={config.summaryColumns.length + 2}>No hay registros guardados para este formato.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
